
import {
  Component, Ref, Vue, Watch,
} from 'vue-property-decorator'
import Widget from '@/components/Widget/Widget.vue'
import MediaPlan from '@/models/MediaPlan'
import ViewModel from '@/models/ViewModel'
import IconAction from '@/components/IconAction/IconAction.vue'
import SearchInput from '@/components/SearchInput/SearchInput.vue'
import DataTable from '@/components/DataTable/index.vue'
import SelectPicker from '@/components/SelectPicker/SelectPicker.vue'
import {
  clone, groupBy, identity, set,
} from 'lodash'
import ExportMediaPlan from '@/pages/Sales/MediaPlan/components/ExportMediaPlan.vue'
import { getModule } from 'vuex-module-decorators'
import SystemtModule from '@/store/SystemModule'
import DataForm from '@/components/DataForm/DataForm.vue'
import WebMessage from '@/models/WebMessage'
import FaqModal from '@/components/FaqModal/FaqModal.vue'
import DropArea from '@/components/DropArea/DropArea.vue'
import { read, utils, writeFile } from 'xlsx'
import moment from 'moment'
import Company from '@/models/Company'
import CompanyPicker from '@/components/CompanyPicker/CompanyPicker.vue'
import options from './options'
import formData from './form'

@Component({
  components: {
    Widget,
    IconAction,
    SearchInput,
    DataTable,
    SelectPicker,
    DataForm,
    FaqModal,
    DropArea,
    CompanyPicker,
  },
})
export default class PixelManagerHome extends ViewModel {
  // dataTable Ref
  @Ref() readonly dataTable!: HTMLFormElement

  @Ref() readonly batchForm: any

  // Target Media Plan during action event
  public media_plan: MediaPlan = new MediaPlan()

  // Total Number of records
  public records: number = 0

  // Loading incicator
  public loading: boolean = false

  public show_filter_helper: boolean = false

  private busy = false

  // Search Input
  public query: string[] = []

  // Selected rows
  public selected: string[] = []

  public import_status: string = ''

  public import_errors: any[] = []

  public import_records: any[] = []

  public agencies: any[] = []

  public publisher_to_check: any[] = []

  public missing_publishers: any[] = []

  public searchMps: any[] = []

  public foundMps: any[] = []

  public file_name: any = null

  public requesting_step = 'check:publisher'

  public is_checking_backend: boolean = true

  public import_fields: any = [
    {
      key: 'name',
      label: 'Name',
      thClass: 'align-middle text-center',
      sortable: true,
      show: true,
      tdClass: 'align-middle text-center',
    },
    {
      key: 'station_id',
      label: 'Station',
      thClass: 'align-middle text-center',
      sortable: true,
      show: true,
      type: 'object',
      object: {
        key: 'station',
        property: 'name',
      },
      tdClass: 'align-middle text-center',
    },
    {
      key: 'advertiser_id',
      label: 'Advertiser',
      thClass: 'align-middle text-center',
      sortable: true,
      show: true,
      type: 'object',
      object: {
        key: 'advertiser',
        property: 'name',
      },
      tdClass: 'align-middle text-center',
    },
    {
      key: 'flight_dates',
      label: 'Flight Dates',
      thClass: 'align-middle text-center',
      sortable: false,
      show: true,
      tdClass: 'align-middle text-center',
    },
    {
      key: 'impressions',
      label: 'Impressions',
      thClass: 'align-middle text-center',
      sortable: false,
      show: true,
      type: 'separator',
      tdClass: 'align-middle text-center',
    },
    {
      key: 'actions',
      label: '',
      thClass: 'align-middle text-center',
      sortable: false,
      show: true,
      tdClass: 'align-middle text-center',
      thStyle: 'min-width: 180px',
    },
  ]

  // dataTable field filters
  public fieldFilters: any = {
    created_at: '',
    updated_at: '',
    type: '',
    status: '',
    lost_category: '',
    products: '',
  }

  // table data
  public table_data: MediaPlan[] = []

  // Used to determine which detail view to show
  public detail_mode = 'schedule'

  // Indicates is component is initialized and ready to load data
  public ready = false

  public temp_mediaplan_batch = new MediaPlan({ status: 'pending_review' })

  public temp_batch_tab: number = 0

  public formFields: any = formData

  public batch_item_modal: boolean = false

  public batch_data: any = []

  public filter_mode: string = 'exclusive'

  @Watch('filter_mode')
  public onFilterModeChange() {
    this.refresh()
  }

  @Watch('fields', { deep: true })
  public onFieldsChange() {
    if (this.ready) {
      this.syncFilters()
    }
  }

  public get hasStation() {
    return this.import_records.every(item => item.station.name && item.station.name.length)
  }

  // Media Plan Module Options (Search options, Action options, etc)
  public get options() {
    return options
  }

  // List of fields, populated on component mount
  public fields: Array<any> = []

  // Return active fields to the table
  public get show_fields() {
    return this.fields.filter((f: any) => f.show)
  }

  public get active_field_keys() {
    return this.show_fields.map((f: any) => f.key)
  }

  // Return Promise with DataTable data
  public rows(context: any): Promise<any> {
    this.loading = true
    const field_filters = Object.keys(this.fieldFilters)
      .filter((key: string) => this.fieldFilters[key] !== '')
      .map((key: string) => `${key}:${this.fieldFilters[key].toLowerCase()}`)

    this.syncFilters()
    return MediaPlan.paginate({
      page_size: context.perPage,
      page: context.currentPage,
      order_by: context.sortBy,
      order: context.sortDesc ? 'desc' : 'asc',
      query: [...context.filter, ...field_filters],
      mode: this.filter_mode,
    }).then(result => {
      this.records = result.records
      this.selected = []
      this.loading = false
      this.table_data = result.data
      return result.data
    })
  }

  // Redirect user to the create page
  public createMediaPlan() {
    this.$router.push('/app/sales/media_plan')
  }

  // Refresh dataTable
  public refresh() {
    this.dataTable.refresh()
  }

  // Reset filters
  public resetFilters() {
    this.fieldFilters = {
      created_at: '',
      updated_at: '',
      type: '',
      status: '',
      products: '',
    }
    this.query = []
    this.filter_mode = 'exclusive'
    this.clearFilters()
    this.refresh()
  }

  // Initialize vars
  public mounted() {
    this.fields = clone(MediaPlan.tableFields)
    this.loadFilters()
  }

  // Store session filters in VUEX
  public syncFilters() {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'media_plan',
      data: { query: this.query, fieldFilters: this.fieldFilters, mode: this.filter_mode },
    })

    system.updateState({
      name: 'fields',
      type: 'media_plan',
      data: this.active_field_keys,
    })
  }

  // Load filters from VUEX if present
  public loadFilters() {
    const system = getModule(SystemtModule)
    let promises: Promise<any>[] = []

    const filter = system.getFilter('media_plan').then((filter: any) => {
      if (filter) {
        this.query = filter.query
        this.fieldFilters = filter.fieldFilters
        this.filter_mode = filter.mode
      }
    })

    const field = system.getFields('media_plan').then((fields: any) => {
      if (fields) {
        this.fields = this.fields.map((field: any) => {
          field.show = fields.includes(field.key)
          return field
        })
      }
    })

    promises.push(filter)
    promises.push(field)

    Promise.all(promises).then(() => {
      this.ready = true
    })
  }

  // Clear VUEX filters state
  public clearFilters() {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'media_plan',
      data: null,
    })
  }

  // Open Batch export dialog
  public exportMediaPlans() {
    if (this.selected.length === 0) {
      WebMessage.error('Please select at least one Media Plan')
      return
    }
    let Component = Vue.extend(ExportMediaPlan)
    let instance = new Component({
      propsData: {
        media_plans: this.table_data.filter(
          (media_plan: MediaPlan) =>
            typeof media_plan.id === 'string' && this.selected.includes(media_plan.id),
        ),
        mode: 'single',
      },
    })
    instance.$mount()
  }

  public batchEdit() {
    if (this.selected.length === 0) {
      WebMessage.error('Please select at least one Media Plan')
      return
    }
    this.$bvModal.show('batch-edit')
  }

  public addLineItem() {
    this.temp_mediaplan_batch.addLineItem()
  }

  public importExcel(files: FileList) {
    this.clearAll()
    if (!files || files.length == 0) return

    if (files.length > 1) {
      WebMessage.error('You can only upload one order file at a time.')
      return
    }

    let file = files[0]

    if (file) {
      this.processOrderFile(file)
    }
  }

  private async processOrderFile(file: any) {
    this.import_records = []
    this.import_status = 'Loading file...'
    this.$bvModal.show('import-loader')

    this.import_errors = []
    let reader = new FileReader()

    reader.onload = async (e: any) => {
      // get data
      let bytes = new Uint8Array(e.target.result)
      let file_name = file.name
      this.file_name = file_name
      /* read workbook */
      let wb = read(bytes)

      const data = utils.sheet_to_json<any>(wb.Sheets[wb.SheetNames[0]])

      let records: any = []
      data.forEach(r => {
        let client = r.CLIENT.substr(0, 3)
        let product = r.PRODUCT.substr(0, 3)
        let estimate = r.ESTIMATE.substr(0, 3)
        let station = r.STATION.substr(0, 4)
        let market = station.substr(2, 4)
        let publisher = station.substr(0, 2)
        let start_at = moment(r.WEEK, 'MM/DD/YYYY')

        records.push({
          client,
          product,
          estimate,
          advertiser: r.CLIENT.substr(3).trim(),
          group_code: `${client}/${product}/${estimate}/${market}`,
          publisher,
          market: r.MARKET,
          duration: r.LEN,
          station,
          impressions: r['PURCH IMPRESSIONS'] * 1000,
          start_at: start_at.format('YYYY-MM-DD'),
          end_at: start_at.clone().add(7, 'days').endOf('day').format('YYYY-MM-DD'),
          update: true,
        })
      })

      let media_plans: MediaPlan[] = []
      let groups = groupBy(records, 'group_code')

      for (let k in groups) {
        let media_plan = new MediaPlan()

        // Sort lines by startime
        let publishers = groupBy(
          groups[k].sort(
            (a: any, b: any) =>
              moment(a.start_at, 'YYYY-MM-DD').unix() - moment(b.start_at, 'YYYY-MM-DD').unix(),
          ),
          'publisher',
        )

        let ref: any = null

        for (let publisher in publishers) {
          let lines = publishers[publisher]

          ref = lines[0]

          media_plan.addLineItem()

          let start_at = lines.reduce((a: any, b: any) =>
            (moment(a.start_at, 'YYYY-MM-DD').unix() < moment(b.start_at, 'YYYY-MM-DD').unix()
              ? a
              : b)).start_at
          let end_at = lines.reduce((a: any, b: any) =>
            (moment(a.end_at, 'YYYY-MM-DD').unix() > moment(b.end_at, 'YYYY-MM-DD').unix() ? a : b)).end_at

          let impressions = lines.reduce((a: any, b: any) => a + b.impressions, 0)

          let item = media_plan.line_items[media_plan.line_items.length - 1]

          item.name = publisher
          item.publisher = publisher // add on module
          item.start_at = start_at
          item.end_at = end_at
          item.impressions = impressions
          this.publisher_to_check.push(publisher)
        }

        media_plan.name = `${ref.client}/${ref.product}/${ref.estimate} ${ref.market} - ${ref.advertiser}`

        media_plan.advertiser = new Company()
        media_plan.advertiser.name = ref.advertiser

        media_plan.station = new Company()
        media_plan.station.type = 'station'
        media_plan.station.name = ref.station

        media_plan.metadata.header.agency_advertiser_code = ref.client
        media_plan.metadata.header.agency_product_code = ref.product
        media_plan.metadata.header.agency_estimate_code = ref.estimate
        media_plan.metadata.station.call_letters = ref.station

        this.searchMps.push({
          client: media_plan.metadata.header.agency_advertiser_code,
          product: media_plan.metadata.header.agency_product_code,
          estimate: media_plan.metadata.header.agency_estimate_code,
          station: media_plan.station.name,
        })

        media_plans.push(media_plan)
      }

      this.import_records = media_plans

      this.is_checking_backend = true

      let codes = await this.checkPublisher()
      if (!codes) {
        await this.checkExistingMP()
      }

      this.import_status = 'done'

      this.is_checking_backend = false
    }

    reader.readAsArrayBuffer(file)
  }

  public confirmImport() {
    this.loading = true

    let payload = {
      agencies: this.agencies, // they are publishers too
      media_plans: this.import_records,
      file_name: this.file_name.replace('.xlsx', ''),
      publishers: this.publisher_to_check,
    }

    MediaPlan.importTrackers(payload).then(response => {
      this.loading = false
      this.$bvModal.hide('import-loader')
      this.clearAll()
      this.refresh()
    })
  }

  public async checkPublisher() {
    try {
      this.requesting_step = 'check:publisher'
      let mapped = this.publisher_to_check.map(name => ({ publisher: name }))
      let res = await Company.searchPublisherByCodes(mapped)

      return new Promise((resolve, reject) => {
        if (res.result.missing) {
          this.missing_publishers = res.result.codes

          this.import_errors.push({
            html: ` 
              <p>- The following publishers don't exist, create them to proced:</p> 
              <span class="text-primary">${this.missing_publishers.join(', ')}</span> <hr/>
            `,
          })

          setTimeout(() => {
            this.is_checking_backend = false
            this.import_status = 'done'
            resolve(res.result.codes)
          }, 2000)
        } else {
          this.missing_publishers = []
        }

        setTimeout(() => {
          resolve(res.result.missing ? res.result.codes : false)
        }, 1500)
      })
    } catch (error) {
      return new Promise((resolve, reject) => {
        resolve(false)
      })
    }
  }

  public async checkExistingMP() {
    this.requesting_step = 'check:media_plan'
    let response = await MediaPlan.checkForExistingMP(this.searchMps)

    if (Array.isArray(response)) {
      this.foundMps = response
    }

    if (
      response.response
      && response.response.status
      && ![200, 201].includes(response.response.status)
    ) {
      this.import_errors.push({
        html: `
            <p>- ${response.response.data.message}</p> 
            `,
      })
    }

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(response)
      }, 1500)
    })
  }

  public doesExist(item: MediaPlan, index: any = null) {
    let found = this.foundMps.find(
      mp =>
        mp.metadata.header.agency_advertiser_code === item.metadata.header.agency_advertiser_code
        && mp.metadata.header.agency_product_code === item.metadata.header.agency_product_code
        && parseInt(mp.metadata.header.agency_estimate_code)
          === parseInt(item.metadata.header.agency_estimate_code)
        && mp.metadata.station.call_letters === item.metadata.station.call_letters,
    )

    if (found && index !== null) {
      this.import_records[index].existing_id = found.id
    }

    return found && found.id && found.id.length > 0
  }

  public clearAll() {
    this.import_errors = []

    this.import_records = []

    this.agencies = []

    this.searchMps = []

    this.foundMps = []

    this.missing_publishers = []

    this.publisher_to_check = []

    this.file_name = null

    this.requesting_step = 'check:publisher'

    this.is_checking_backend = true
  }
}
